// LicenseOrgDetailPage.tsx — Single-org overview: metadata,
// at-a-glance stats, and a 5-row recent-activity preview.

import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFirestoreQuery, invalidateCache } from '@/hooks/useFirestoreQuery';
import { getLicenseOrg } from '@/services/firestore/licenseOrgs';
import {
  listActiveMembers,
  listActiveSeatAllocations,
} from '@/services/firestore/members';
import { listActiveInvites } from '@/services/firestore/invites';
import { fetchRecentAudit } from '@/services/firestore/audit';
import { mintLicenseSeatCode } from '@/services/functions/mintLicenseSeatCode';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Modal } from '@/components/Modal';
import { SkeletonDetail } from '@/components/SkeletonRow';
import { ErrorState } from '@/components/ErrorState';
import { ActionPill, OutcomePill, StatusPill } from '@/components/pills';
import { fmtDate, fmtRel, memberLabel } from '@/utils/format';
import type { AuditEvent, LicenseOrg } from '@/types/domain';

interface Stats {
  members: number;
  activeSeats: number;
  activeInvites: number;
  auditEvents: number | '5+';
}

interface DetailPayload {
  org: LicenseOrg | null;
  stats: Stats;
  recent: AuditEvent[];
}

export function LicenseOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const cacheKey = orgId ? `licenseOrg:detail:${orgId}` : null;

  const fetcher = useCallback(async (): Promise<DetailPayload> => {
    if (!orgId) return { org: null, stats: { members: 0, activeSeats: 0, activeInvites: 0, auditEvents: 0 }, recent: [] };
    const [org, members, seats, invites, recent] = await Promise.all([
      getLicenseOrg(orgId),
      listActiveMembers(orgId),
      listActiveSeatAllocations(orgId),
      listActiveInvites(orgId),
      fetchRecentAudit(orgId),
    ]);
    return {
      org,
      stats: {
        members: members.length,
        activeSeats: seats.length,
        activeInvites: invites.length,
        auditEvents: recent.length >= 5 ? '5+' : recent.length,
      },
      recent,
    };
  }, [orgId]);

  const { data, loading, error, refetch } = useFirestoreQuery(cacheKey, fetcher);
  const [mintOpen, setMintOpen] = useState(false);

  if (!orgId) return <ErrorState message="Missing org id in URL" />;
  if (loading && !data) return <SkeletonDetail />;
  if (error) return <ErrorState message={error.message} />;
  if (!data || !data.org) {
    return <ErrorState message={`License org "${orgId}" not found`} />;
  }

  const { org, stats, recent } = data;
  const orgPath = `/license-orgs/${encodeURIComponent(orgId)}`;

  return (
    <>
      <Breadcrumb
        crumbs={[
          { label: 'License Orgs', to: '/license-orgs' },
          org.name || orgId,
        ]}
      />
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">{org.name || orgId}</h1>
          <div className="console-page-subtitle">License org · {orgId}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/members`}>Members</Link>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/invites`}>Invites</Link>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/audit`}>Audit log</Link>
          <button
            className="console-btn console-btn-primary"
            type="button"
            onClick={() => setMintOpen(true)}
          >
            <span className="material-icons">vpn_key</span> Mint seat code
          </button>
        </div>
      </header>
      {mintOpen && (
        <MintSeatCodeModal
          orgId={orgId}
          onClose={() => setMintOpen(false)}
          onSuccess={() => {
            invalidateCache(`licenseOrg:detail:${orgId}`);
            invalidateCache('licenseOrgs:list');
            refetch();
          }}
        />
      )}

      <div className="console-card">
        <div className="console-card-title">Org metadata</div>
        <div className="console-card-grid">
          <Field label="Status">{<StatusPill status={org.status} />}</Field>
          <Field label="Org id"><code>{orgId}</code></Field>
          <Field label="Owner"><code>{org.ownerUid || '-'}</code></Field>
          <Field label="Created">{fmtDate(org.createdAt)}</Field>
          <Field label="Capacity">
            {org.seatCapacity != null ? `${org.seatCapacity} seats` : '-'}
          </Field>
        </div>
      </div>

      <div className="console-card">
        <div className="console-card-title">At a glance</div>
        <div className="console-stat-grid">
          <Stat label="Active members" value={stats.members} to={`${orgPath}/members`} />
          <Stat
            label={
              org.seatCapacity != null
                ? `Active seats / ${org.seatCapacity}`
                : 'Active seats'
            }
            value={stats.activeSeats}
            to={`${orgPath}/members`}
          />
          <Stat label="Active invites" value={stats.activeInvites} to={`${orgPath}/invites`} />
          <Stat label="Audit events" value={stats.auditEvents} to={`${orgPath}/audit`} />
        </div>
      </div>

      <RecentActivity recent={recent} orgPath={orgPath} />
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="console-card-field">
      <span className="console-card-label">{label}</span>
      <span className="console-card-value">{children}</span>
    </div>
  );
}

function Stat({ label, value, to }: { label: string; value: number | string; to: string }) {
  return (
    <Link to={to} className="console-stat" style={{ textDecoration: 'none' }}>
      <div className="console-stat-label">{label}</div>
      <div className="console-stat-value">{value}</div>
    </Link>
  );
}

function RecentActivity({ recent, orgPath }: { recent: AuditEvent[]; orgPath: string }) {
  return (
    <div className="console-card">
      <div className="console-card-title">Recent activity</div>
      {recent.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No events yet.</div>
      ) : (
        <div className="console-activity">
          {recent.map((r) => (
            <div key={r.id} className="console-activity-row">
              <div className="console-activity-time">{fmtRel(r.tsServer)}</div>
              <div className="console-activity-body">
                <ActionPill action={String(r.action)} />
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text-muted)' }}>
                  {memberLabel(r.actorUid)}
                </span>
                {r.reasonCode && (
                  <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--error)' }}>
                    {r.reasonCode}
                  </code>
                )}
              </div>
              <OutcomePill outcome={String(r.outcome)} />
            </div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Link className="console-btn console-btn-ghost" to={`${orgPath}/audit`}>View all events</Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mint LSEAT- seat code modal
// ---------------------------------------------------------------------------

interface MintSeatResult {
  code: string;
  productId: string;
  maxUses: number;
  expiresAt?: string;
}

function MintSeatCodeModal({
  orgId,
  onClose,
  onSuccess,
}: {
  orgId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [productId, setProductId] = useState('community_pack_20seat');
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInput, setExpiresInput] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MintSeatResult | null>(null);

  async function handleMint() {
    if (submitting) return;
    setError(null);
    if (!productId.trim()) {
      setError('Product id is required.');
      return;
    }
    if (!Number.isFinite(maxUses) || maxUses < 1 || maxUses > 1000) {
      setError('Max uses must be a number between 1 and 1000.');
      return;
    }
    let expiresAtIso: string | null = null;
    if (expiresInput) {
      const dt = new Date(expiresInput);
      if (Number.isNaN(dt.getTime())) {
        setError('Invalid expiry date.');
        return;
      }
      if (dt.getTime() < Date.now()) {
        setError('Expiry must be in the future.');
        return;
      }
      expiresAtIso = dt.toISOString();
    }
    setSubmitting(true);
    try {
      const data = await mintLicenseSeatCode({
        licenseOrgId: orgId,
        productId: productId.trim(),
        maxUses,
        expiresAtIso,
        note: note.trim() || null,
      });
      setResult(data);
      onSuccess();
    } catch (err) {
      setError(
        (err as { message?: string })?.message ?? 'Could not mint seat code',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return <MintSeatRevealModal result={result} onClose={onClose} />;
  }

  return (
    <Modal open onClose={onClose} ariaLabel="Mint seat code">
      <div className="console-modal-title">Mint a seat code</div>
      <div className="console-modal-description">
        Generates an <strong>LSEAT-</strong> code the operator can hand to a member out-of-band.
        The member types it into the mobile app's "Have an unlock code?" tile to redeem.
        For link-based onboarding, use Invites instead.
      </div>

      <div className="console-modal-meta">
        <div>
          <strong>Org</strong><br />
          <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{orgId}</code>
        </div>
      </div>

      <div className="console-form-field">
        <label className="console-form-label" htmlFor="seat-product-id">Product id</label>
        <input
          id="seat-product-id"
          className="console-form-input"
          type="text"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
          maxLength={64}
          placeholder="community_pack_20seat"
        />
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1 }}>
          <label className="console-form-label" htmlFor="seat-max-uses">Max uses</label>
          <input
            id="seat-max-uses"
            className="console-form-input"
            type="number"
            min={1}
            max={1000}
            value={maxUses}
            onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 1)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label className="console-form-label" htmlFor="seat-expires">Expires (optional)</label>
          <input
            id="seat-expires"
            className="console-form-input"
            type="datetime-local"
            value={expiresInput}
            onChange={(e) => setExpiresInput(e.target.value)}
          />
        </div>
      </div>

      <div className="console-form-field" style={{ marginTop: 12 }}>
        <label className="console-form-label" htmlFor="seat-note">Note (optional)</label>
        <input
          id="seat-note"
          className="console-form-input"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
          placeholder="e.g. Acme onboarding batch 1"
        />
      </div>

      {error && (
        <div className="console-modal-error" role="alert">
          {error}
        </div>
      )}

      <div className="console-modal-actions">
        <button className="console-btn console-btn-ghost" type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="console-btn console-btn-primary"
          type="button"
          onClick={handleMint}
          disabled={submitting}
        >
          {submitting ? 'Minting…' : 'Mint seat code'}
        </button>
      </div>
    </Modal>
  );
}

function MintSeatRevealModal({
  result,
  onClose,
}: {
  result: MintSeatResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(result.code).catch(() => {});
      setCopied(true);
    }
  }
  let expiresLabel = 'no expiry';
  if (result.expiresAt) {
    try {
      expiresLabel = `expires ${new Date(result.expiresAt).toLocaleString()}`;
    } catch {
      // ignore malformed
    }
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Seat code minted">
      <div className="console-modal-title">Seat code minted. Copy it now.</div>
      <div className="console-modal-description">
        Shown <strong>once</strong>. The server never returns the plaintext after this dialog closes.
      </div>
      <div className="console-code-reveal">
        <span className="console-code-reveal-label">LSEAT code</span>
        <span className="console-code-reveal-value">{result.code}</span>
        <span className="console-code-reveal-warning">
          Product: {result.productId} · max uses: {result.maxUses} · {expiresLabel}
        </span>
      </div>
      <div className="console-modal-actions">
        <button className="console-btn console-btn-ghost" type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button className="console-btn console-btn-primary" type="button" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}
